import { BordOneToneIcon, TbordOneToneIcon } from 'bord-design-system';
import BordStatus from 'components/NudosComponents/BordStatus/BordStatus';
import { STATUS_VARIANTS, statusVariants } from 'components/NudosComponents/BordStatus/BordStatus.type';
import { IQuotePurchaseTrackingOrder } from 'types/quoteDetails.types';
import { formatShortDateNoPadDay } from 'utils/formatDatesAndHours';
import { formatCost } from 'utils/formatNumbers';
import './QuotePurchaseTrackingCard.scss';

interface IQuotePurchaseTrackingCard {
  trackingData: IQuotePurchaseTrackingOrder;
}

/**
 * Card de seguimiento de compra que muestra una orden con sus dispositivos asociados.
 */
const QuotePurchaseTrackingCard = ({ trackingData }: IQuotePurchaseTrackingCard) => {
  const statusOptions: Record<
    string,
    { label: string; variant: statusVariants; icon: TbordOneToneIcon; iconBg: string; stroke: string }
  > = {
    noOrder: {
      label: 'Sin orden',
      variant: STATUS_VARIANTS.default,
      icon: 'clock',
      iconBg: '#F1F1F1',
      stroke: '#6F737D'
    },
    pendingPayment: {
      label: 'Pendiente de pago',
      variant: STATUS_VARIANTS.informative,
      icon: 'loader',
      iconBg: 'rgba(35, 165, 255, 0.10)',
      stroke: '#4096F0'
    },
    paid: {
      label: 'Pagada',
      variant: STATUS_VARIANTS.success,
      icon: 'checkCircle',
      iconBg: 'rgba(9, 164, 50, 0.10)',
      stroke: '#00994F'
    },
    overdue: {
      label: 'Vencida',
      variant: STATUS_VARIANTS.danger,
      icon: 'xCircle',
      iconBg: 'rgba(252, 84, 61, 0.10)',
      stroke: '#DE072E'
    }
  };

  const getTrackingStatus = () => {
    if (!trackingData?.orderGenerated) return statusOptions.noOrder;
    if (trackingData?.isPaid) return statusOptions.paid;
    const isExpired = new Date(trackingData?.expiresAt) <= new Date();
    return isExpired ? statusOptions.overdue : statusOptions.pendingPayment;
  };

  const currentStatus = getTrackingStatus();
  const orderLabel = trackingData?.orderGenerated ? `ORD-${trackingData?.orderGenerated}` : 'Sin orden generada';
  const formatAmount = (value?: number) => (value !== undefined ? `$${formatCost(value)} USD` : undefined);

  const handleDeviceSkuClick = (productId?: number, categoryId?: number) => {
    if (!productId || !categoryId) return;
    window.open(`/catalog/create-product/${categoryId}?productId=${productId}`, '_blank');
  };

  const handleOrderClick = (orderGenerated?: number) => {
    if (!orderGenerated) return;
    window.open(`/order/details/${orderGenerated}`, '_blank');
  };

  return (
    <div className="quotePurchaseTrackingCard">
      <div className="cardHeader">
        <div className="headerLeft">
          <div className="iconWrapper" style={{ backgroundColor: currentStatus?.iconBg }}>
            <div className="iconInner" style={{ backgroundColor: currentStatus?.iconBg }}>
              <BordOneToneIcon variant={currentStatus?.icon} standardSize={16} stroke={currentStatus?.stroke} />
            </div>
          </div>
          <div className="orderInfo">
            <div
              className={`orderLabel${trackingData?.orderGenerated ? ' clickable' : ''}`}
              onClick={() => handleOrderClick(trackingData?.orderGenerated)}
            >
              {orderLabel}
              {trackingData?.orderGenerated && (
                <BordOneToneIcon variant="externalLink" standardSize={12} stroke="#00B188" />
              )}
            </div>
            {trackingData?.createdAt && trackingData?.expiresAt && (
              <div className="orderDates">
                Creada el {formatShortDateNoPadDay(trackingData.createdAt)} – vence el{' '}
                {formatShortDateNoPadDay(trackingData.expiresAt)}
              </div>
            )}
          </div>
        </div>
        <div className="headerRight">
          <div className="toolCount">
            <div className="toolCountLabel">Herramientas</div>
            <div className="toolCountValue">{trackingData?.numberOfTools}</div>
          </div>
          {trackingData?.totalAmount && (
            <div className="orderTotal">
              <div className="orderTotalLabel">Monto total</div>
              <div className="orderTotalValue">{formatAmount(trackingData.totalAmount)}</div>
            </div>
          )}
          <BordStatus variant={currentStatus?.variant} label={currentStatus?.label} />
        </div>
      </div>
      <div className="deviceList">
        {trackingData?.items?.map(device => (
          <div key={device.id} className="deviceRow">
            <div className="deviceInfo">
              <div className="deviceName">{device?.productName}</div>
              <div
                className={`deviceSku${device?.productId && device?.categoryId ? ' clickable' : ''}`}
                onClick={() => handleDeviceSkuClick(device?.productId, device?.categoryId)}
              >
                {device?.sku}
                {device?.productId && device?.categoryId && (
                  <BordOneToneIcon variant="externalLink" standardSize={12} stroke="#00B188" />
                )}
              </div>
            </div>
            <div className="deviceCols">
              <div className="deviceUnits">
                <div className="unitsLabel">Unidades</div>
                <div className="unitsValue">{device?.units}</div>
              </div>
              {device?.totalAmount && (
                <div className="deviceTotal">
                  <div className="deviceTotalLabel">Total</div>
                  <div className="deviceTotalValue">{formatAmount(device.totalAmount)}</div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default QuotePurchaseTrackingCard;
